import { formatBrl } from './money.js';

/**
 * Commercial modules sold on top of (or instead of) the per-vacancy plan.
 * Single catalog so checkout, self-serve toggle, backoffice MRR and composition
 * all agree.
 *
 * Adding a module = catalog entry + (for usage) a meter + a feature gate.
 * No new billing routes or parking_lots columns once on lot_modules.
 */

export type ModuleKey =
  | 'damage_photos'
  | 'valet'
  | 'sms'
  | 'email'
  | 'plate_ocr'
  | 'online_checkout'
  | 'barrier'
  | 'printer'
  | 'convenio'
  | 'nfse';
/** @deprecated use ModuleKey */
export type AddonKey = ModuleKey;

export type ModuleBilling =
  | {
      kind: 'flat';
      priceCents: number;
      trialDays: number;
      /**
       * Per-unit price billed on top of the monthly fee, counted in the month
       * and charged on the next invoice (NFS-e: one unit per authorized note).
       * The trial covers the monthly fee only: units are always billed.
       */
      usage?: { perUnitCents: number; unitLabel: string };
    }
  | {
      kind: 'usage';
      perUnitCents: number;
      unitLabel: string;
      trialDays: number;
    }
  | {
      /** Gate only — never billed (e.g. free e-mail module). */
      kind: 'free';
    };

export interface ModuleDef {
  key: ModuleKey;
  label: string;
  description: string;
  billing: ModuleBilling;
  /** One-time install fee in cents. Not part of MRR. */
  setupCents?: number;
  /** Quoted by sales — owners cannot self-serve enable. */
  salesQuoted?: boolean;
  /** Can be the only product on a lot (no parking plan). */
  standalone?: boolean;
  monthlySettingsKey?: string;
  unitSettingsKey?: string;
  /** platform_settings key for setup fee override (informational). */
  setupSettingsKey?: string;
}

/**
 * Per message of the WhatsApp and SMS module (#40, #718). The lot picks one
 * channel, and both cost the same per message.
 */
export const SMS_SELL_PRICE_CENTS_DEFAULT = 10;
/** Channel of the WhatsApp and SMS module's alerts: a WhatsApp template or GTI SMS (#40). */
export type MessageChannel = 'sms' | 'whatsapp';

/** How screens name the channel: "SMS enviado", "Reenviar pelo WhatsApp". */
export const messageChannelLabel = (channel: MessageChannel | null | undefined): string =>
  channel === 'whatsapp' ? 'WhatsApp' : 'SMS';
/** Per confirmed in-app charge (valet call, tip, Asaas monthly link). */
export const CHECKOUT_SELL_PRICE_CENTS_DEFAULT = 49;
/**
 * NFS-e module monthly fee. Each note's fee is charged by Asaas straight to
 * the lot's Recebimentos subaccount, not on our invoice.
 */
export const NFSE_PRICE_CENTS_DEFAULT = 2900;
/** One-time cabine install (exe + Elgin/Bematech). */
export const PRINTER_SETUP_CENTS_DEFAULT = 19_900;
/** One-time iDBox wiring + pairing. */
export const BARRIER_SETUP_CENTS_DEFAULT = 39_900;

export const MODULES: readonly ModuleDef[] = [
  {
    key: 'damage_photos',
    label: 'Fotos de avaria',
    description:
      'Registre o estado do carro na entrada, com foto e descrição. O cliente vê no recibo.',
    billing: { kind: 'flat', priceCents: 2490, trialDays: 30 },
    monthlySettingsKey: 'addon.damage_photos_cents',
  },
  {
    key: 'valet',
    label: 'Valet',
    description:
      'Manobrista estaciona pelo cliente, com chamado do carro pelo link. Carros ilimitados.',
    billing: { kind: 'flat', priceCents: 4990, trialDays: 30 },
    standalone: true,
    monthlySettingsKey: 'addon.valet_floor_cents',
  },
  {
    key: 'sms',
    label: 'WhatsApp e SMS',
    description:
      'O link do ticket, os lembretes e os avisos chegam ao cliente pelo WhatsApp ou por SMS, no canal que o dono escolher. Módulo grátis; cada mensagem entra na fatura, pelo mesmo preço nos dois canais.',
    billing: {
      kind: 'usage',
      perUnitCents: SMS_SELL_PRICE_CENTS_DEFAULT,
      unitLabel: 'mensagem',
      trialDays: 0,
    },
    unitSettingsKey: 'sms.sell_price_cents',
  },
  {
    key: 'email',
    label: 'E-mail',
    description:
      'Link do ticket e lembretes de mensalista por e-mail. Módulo grátis; e-mail do cliente é opcional.',
    billing: { kind: 'free' },
  },
  {
    key: 'plate_ocr',
    label: 'Leitura de placa (OCR)',
    description:
      'Operador tira foto da placa e o sistema sugere a leitura. Digitação manual continua grátis.',
    billing: { kind: 'flat', priceCents: 1990, trialDays: 30 },
    monthlySettingsKey: 'addon.plate_ocr_cents',
  },
  {
    key: 'online_checkout',
    label: 'Cobrança online',
    description:
      'Cliente paga Pix ou cartão no link do ticket e da mensalidade. O dinheiro cai na conta do estacionamento. Sem o módulo, o acerto continua no caixa.',
    billing: {
      kind: 'usage',
      perUnitCents: CHECKOUT_SELL_PRICE_CENTS_DEFAULT,
      unitLabel: 'cobrança',
      trialDays: 0,
    },
    unitSettingsKey: 'checkout.sell_price_cents',
  },
  {
    key: 'barrier',
    label: 'Cancela',
    description:
      'Abre a cancela pelo celular. Um iDBox no pátio pulsa o relé; qualquer motor com botoeira de abertura na ponta.',
    billing: { kind: 'flat', priceCents: 3990, trialDays: 30 },
    setupCents: BARRIER_SETUP_CENTS_DEFAULT,
    salesQuoted: true,
    monthlySettingsKey: 'addon.barrier_cents',
    setupSettingsKey: 'addon.barrier_setup_cents',
  },
  {
    key: 'printer',
    label: 'Impressora',
    description:
      'Ticket de papel na cabine (Elgin ou Bematech). O celular do operador não imprime sozinho.',
    billing: { kind: 'flat', priceCents: 1990, trialDays: 30 },
    setupCents: PRINTER_SETUP_CENTS_DEFAULT,
    salesQuoted: true,
    monthlySettingsKey: 'addon.printer_cents',
    setupSettingsKey: 'addon.printer_setup_cents',
  },
  {
    key: 'convenio',
    label: 'Convênio com lojas',
    description:
      'A loja parceira paga parte do estacionamento de quem compra nela. O caixa da loja valida o ticket pelo celular e o extrato do mês mostra quanto cada loja deve.',
    // Sem limite de lojas: o preço não cresce com o número de convênios.
    billing: { kind: 'flat', priceCents: 1990, trialDays: 30 },
    monthlySettingsKey: 'addon.convenio_cents',
  },
  {
    key: 'nfse',
    label: 'Nota fiscal (NFS-e)',
    description:
      'Emite a nota fiscal de serviço pela conta de Recebimentos, quando o cliente pede ou em todo pagamento. A taxa de cada nota o Asaas desconta da própria conta de Recebimentos.',
    billing: { kind: 'flat', priceCents: NFSE_PRICE_CENTS_DEFAULT, trialDays: 30 },
    monthlySettingsKey: 'addon.nfse_cents',
  },
] as const;

/** @deprecated use MODULES */
export const ADDONS = MODULES;

/** Legacy flat shape for older call sites that read priceCents/trialDays. */
export interface AddonDef {
  key: ModuleKey;
  label: string;
  priceCents: number;
  trialDays: number;
  usage?: { perUnitCents: number; unitLabel: string };
}

export const asAddonDef = (def: ModuleDef): AddonDef => {
  if (def.billing.kind === 'flat') {
    return {
      key: def.key,
      label: def.label,
      priceCents: def.billing.priceCents,
      trialDays: def.billing.trialDays,
      ...(def.billing.usage ? { usage: { ...def.billing.usage } } : {}),
    };
  }
  if (def.billing.kind === 'usage') {
    return {
      key: def.key,
      label: def.label,
      priceCents: 0,
      trialDays: def.billing.trialDays,
      usage: {
        perUnitCents: def.billing.perUnitCents,
        unitLabel: def.billing.unitLabel,
      },
    };
  }
  // free
  return { key: def.key, label: def.label, priceCents: 0, trialDays: 0 };
};

export const getModule = (key: ModuleKey): ModuleDef | undefined =>
  MODULES.find((m) => m.key === key);

/**
 * Legacy view with top-level priceCents/trialDays for older call sites.
 * Prefer getModule() for billing.kind-aware code.
 */
export const getAddon = (key: ModuleKey): AddonDef | undefined => {
  const m = getModule(key);
  return m ? asAddonDef(m) : undefined;
};

export interface ModulePriceOverride {
  key: ModuleKey;
  priceCents: number;
  /**
   * Which price the override replaces. Only matters for a flat module that
   * also has a per-unit price; absent means the module's only price.
   */
  part?: 'monthly' | 'unit';
}

/** @deprecated use ModulePriceOverride */
export type AddonPriceOverride = ModulePriceOverride;

export const resolveFlatPriceCents = (
  def: ModuleDef,
  overrides?: readonly ModulePriceOverride[],
): number => {
  if (def.billing.kind !== 'flat') return 0;
  const o = overrides?.find((x) => x.key === def.key && x.part !== 'unit');
  return o ? o.priceCents : def.billing.priceCents;
};

export const resolveUnitPriceCents = (
  def: ModuleDef,
  overrides?: readonly ModulePriceOverride[],
): number => {
  if (def.billing.kind === 'flat') {
    if (!def.billing.usage) return 0;
    const o = overrides?.find((x) => x.key === def.key && x.part === 'unit');
    return o ? o.priceCents : def.billing.usage.perUnitCents;
  }
  if (def.billing.kind !== 'usage') return 0;
  const o = overrides?.find((x) => x.key === def.key);
  return o ? o.priceCents : def.billing.perUnitCents;
};

/** True when the module bills per unit, alone or on top of a monthly fee. */
export const hasUnitPrice = (def: ModuleDef): boolean =>
  def.billing.kind === 'usage' || (def.billing.kind === 'flat' && Boolean(def.billing.usage));

/** Unit label of a module that bills per unit ('mensagem', 'cobrança', 'nota'). */
export const moduleUnitLabel = (def: ModuleDef): string | null => {
  if (def.billing.kind === 'usage') return def.billing.unitLabel;
  if (def.billing.kind === 'flat') return def.billing.usage?.unitLabel ?? null;
  return null;
};

export const resolveSmsSellPriceCents = (overrides?: readonly ModulePriceOverride[]): number => {
  const def = getModule('sms');
  if (!def) return SMS_SELL_PRICE_CENTS_DEFAULT;
  return resolveUnitPriceCents(def, overrides);
};

export const usageAmountCents = (units: number, perUnitCents: number): number =>
  Math.max(0, Math.trunc(units)) * Math.max(0, Math.trunc(perUnitCents));

/** @deprecated use usageAmountCents */
export const smsUsageAmountCents = (
  units: number,
  sellPriceCents: number = SMS_SELL_PRICE_CENTS_DEFAULT,
): number => usageAmountCents(units, sellPriceCents);

export const moduleTrialDays = (def: ModuleDef): number =>
  def.billing.kind === 'flat' || def.billing.kind === 'usage' ? def.billing.trialDays : 0;

/** True when the module never contributes to the subscription total. */
export const isFreeModule = (def: ModuleDef): boolean =>
  def.billing.kind === 'free' ||
  (def.billing.kind === 'flat' && def.billing.priceCents === 0 && def.billing.trialDays === 0);

export const moduleCatalogMonthlyCents = (
  def: ModuleDef,
  overrides?: readonly ModulePriceOverride[],
): number => {
  if (def.billing.kind === 'flat') return resolveFlatPriceCents(def, overrides);
  return 0;
};

export const moduleSetupCents = (def: ModuleDef): number => def.setupCents ?? 0;

/** True when only commercial/ops may enable (no owner self-serve). */
export const isSalesQuotedModule = (def: ModuleDef): boolean => Boolean(def.salesQuoted);

export interface ModuleLine {
  key: ModuleKey;
  label: string;
  amountCents: number;
}

/** @deprecated use ModuleLine */
export type AddonLine = ModuleLine;

export interface ModuleTotals {
  addonsPriceCents: number;
  lines: ModuleLine[];
}

/** @deprecated use ModuleTotals */
export type AddonTotals = ModuleTotals;

export interface EnabledModule {
  key: ModuleKey;
  trialing?: boolean;
  priceCentsOverride?: number;
  /**
   * Last month's units of a flat module with a per-unit price, already
   * priced. Billed on top of the monthly fee, also during the trial.
   */
  usageCents?: number;
}

/** @deprecated use EnabledModule */
export type EnabledAddon = EnabledModule;

export const sumModules = (
  enabled: readonly EnabledModule[],
  overrides?: readonly ModulePriceOverride[],
): ModuleTotals => {
  let addonsPriceCents = 0;
  const lines: ModuleLine[] = [];
  for (const item of enabled) {
    const def = getModule(item.key);
    if (!def) continue;
    const catalog = moduleCatalogMonthlyCents(def, overrides);
    const priceCents = item.priceCentsOverride ?? catalog;
    const usage = Math.max(0, item.usageCents ?? 0);
    const billed = (item.trialing ? 0 : priceCents) + usage;
    addonsPriceCents += billed;
    let label = def.label;
    if (item.trialing) label = `${def.label} (em teste)`;
    else if (def.billing.kind === 'usage' && billed > 0) label = `${def.label} (uso)`;
    if (usage > 0 && def.billing.kind === 'flat') label = `${label} + uso`;
    lines.push({ key: def.key, label, amountCents: billed });
  }
  return { addonsPriceCents, lines };
};

/** @deprecated use sumModules */
export const sumAddons = sumModules;

export interface SubscriptionTotal {
  vacanciesPriceCents: number;
  addonsPriceCents: number;
  totalCents: number;
  addonLines: ModuleLine[];
}

export const composeSubscriptionTotal = (
  vacanciesPriceCents: number,
  enabledModules: readonly EnabledModule[],
  overrides?: readonly ModulePriceOverride[],
): SubscriptionTotal => {
  const { addonsPriceCents, lines } = sumModules(enabledModules, overrides);
  return {
    vacanciesPriceCents,
    addonsPriceCents,
    totalCents: vacanciesPriceCents + addonsPriceCents,
    addonLines: lines,
  };
};

export const moduleLabel = (key: ModuleKey, overrides?: readonly ModulePriceOverride[]): string => {
  const def = getModule(key);
  if (!def) return key;
  if (def.billing.kind === 'usage') {
    return `${def.label} — ${formatBrl(resolveUnitPriceCents(def, overrides))}/un`;
  }
  if (def.billing.kind === 'flat') {
    const monthly = `${def.label} — ${formatBrl(resolveFlatPriceCents(def, overrides))}/mês`;
    if (!def.billing.usage) return monthly;
    return `${monthly} + ${formatBrl(resolveUnitPriceCents(def, overrides))}/${def.billing.usage.unitLabel}`;
  }
  return def.label;
};

/** @deprecated use moduleLabel */
export const addonLabel = moduleLabel;
